import { ApiError, type CaptiveRenewal, type PublicBusContext, type RedemptionInput, type RedemptionResult } from '../types';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '/api';
const useMock = import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_API === 'true';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  const payload = await response.json().catch(() => ({})) as { data?: T; code?: string; message?: string } & T;
  if (!response.ok) throw new ApiError(payload.code ?? 'REQUEST_FAILED', payload.message ?? 'No pudimos completar la solicitud.', response.status);
  return payload.data ?? payload;
}

function isBusContext(value: unknown): value is PublicBusContext {
  if (!value || typeof value !== 'object') return false;
  const context = value as Partial<PublicBusContext>;
  return typeof context.nasIdentifier === 'string'
    && typeof context.captiveLoginUrl === 'string'
    && typeof context.bus?.id === 'number'
    && typeof context.bus?.nombre === 'string'
    && typeof context.empresa?.nombre === 'string'
    && typeof context.empresa?.colorPrimario === 'string'
    && typeof context.empresa?.colorSecundario === 'string';
}

const demoContext: PublicBusContext = {
  nasIdentifier: 'waybus-bus01',
  captiveLoginUrl: 'http://10.81.1.1/login',
  bus: { id: 1, nombre: 'Bus 306', ruta: 'Lima — Arequipa', totalAsientos: 43, pisos: 2 },
  empresa: { nombre: 'WayBus', slug: 'waybus', logoUrl: '/branding/waybus/logo.svg', colorPrimario: '#69229A', colorSecundario: '#F06A22' },
};

export const portalApi = {
  async getBusByNas(nasIdentifier: string): Promise<PublicBusContext> {
    if (useMock) return demoContext;
    const context = await request<PublicBusContext>(`/public/bus/${encodeURIComponent(nasIdentifier)}`);
    if (!isBusContext(context)) throw new ApiError('INVALID_BUS_CONTEXT', 'El bus devolvió una configuración no válida.', 502);
    return context;
  },
  async renewContext(path: string): Promise<CaptiveRenewal> {
    const response = await fetch(path, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const payload = await response.json().catch(() => ({})) as Partial<CaptiveRenewal>;
    if (!response.ok || typeof payload.context !== 'string' || typeof payload.expiresAt !== 'string' || typeof payload.renew !== 'string') {
      throw new ApiError('CAPTIVE_CONTEXT_RENEWAL_FAILED', 'No pudimos renovar tu acceso WiFi.', response.status || 502);
    }
    return payload as CaptiveRenewal;
  },
  async getBus(contextToken: string): Promise<PublicBusContext> {
    if (useMock) return demoContext;
    const context = await request<PublicBusContext>(`/public/captive/context?token=${encodeURIComponent(contextToken)}`);
    if (!isBusContext(context)) {
      throw new ApiError('INVALID_BUS_CONTEXT', 'El punto WiFi devolvió un contexto no válido.', 502);
    }
    return context;
  },
  async redeem(input: RedemptionInput): Promise<RedemptionResult> {
    if (useMock) {
      await new Promise((resolve) => setTimeout(resolve, 650));
      if (input.codigo === 'USED00') throw new ApiError('VOUCHER_ALREADY_USED', 'Este código ya fue utilizado.', 409);
      return { success: true, activationId: 'demo-activation', statusToken: 'demo-status-token', status: 'PENDING', plan: 'WiFi gratuito', bus: demoContext.bus.nombre, ruta: demoContext.bus.ruta, asiento: 12 };
    }
    return request('/public/voucher/canjear', { method: 'POST', body: JSON.stringify(input) });
  },
  activationStatus: (activationId: string, token: string): Promise<Pick<RedemptionResult, 'status' | 'posibleConflicto' | 'accessExpiresAt'>> => useMock ? Promise.resolve({ status: 'ACTIVATED', accessExpiresAt: new Date(Date.now() + 43_200_000).toISOString() }) : request(`/public/activations/${encodeURIComponent(activationId)}/status?token=${encodeURIComponent(token)}`),
};
